import axios from 'axios';

import { getAllWheels, getWheelById } from '../data/mockWheels';
import * as databaseIntegration from './databaseIntegration';
import { auth } from './firebase';

// Flag to determine if we should use mock data
const USE_MOCK_DATA =
  process.env.REACT_APP_USE_MOCK_DATA === 'true' || process.env.NODE_ENV === 'development';

// Create an axios instance with default config
const api = axios.create({
  baseURL:
    process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
    'https://us-central1-spoil-engine.cloudfunctions.net/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include auth token if available
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  response => response,
  error => {
    // Handle specific error cases
    if (error.response) {
      // Server responded with a status code outside of 2xx range
      console.error('API Error:', error.response.data);
      // Handle 401 Unauthorized
      if (error.response.status === 401) {
        // Clear token and redirect to home
        localStorage.removeItem('access_token');
        window.location.href = '/';
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request
      console.error('Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const wheelsApi = {
  getAll: async () => {
    if (USE_MOCK_DATA) {
      return {
        data: getAllWheels(),
        status: 200,
        statusText: 'OK (Mock Data)',
        headers: {},
        config: {},
      };
    }

    try {
      const response = await api.get('/wheels');
      return response;
    } catch (error) {
      // If API call fails, return mock data in the same format
      return {
        data: getAllWheels(),
        status: 200,
        statusText: 'OK (Mock Data)',
        headers: {},
        config: {},
      };
    }
  },

  getById: async (id: string) => {
    // First check if the user is authenticated and try to get from Firebase
    if (auth.currentUser) {
      try {
        const firebaseWheel = await databaseIntegration.getWheel(id);
        if (firebaseWheel) {
          return {
            data: firebaseWheel,
            status: 200,
            statusText: 'OK (Firebase)',
            headers: {},
            config: {},
          };
        }
      } catch (error) {
        // Silently continue to other data sources if Firebase fails
      }
    }

    // If not found in Firebase or not authenticated, try mock data
    if (USE_MOCK_DATA) {
      const wheel = getWheelById(id);
      if (!wheel) {
        throw new Error('Wheel not found');
      }
      return {
        data: wheel,
        status: 200,
        statusText: 'OK (Mock Data)',
        headers: {},
        config: {},
      };
    }

    try {
      const response = await api.get(`/wheels/${id}`);
      return response;
    } catch (error) {
      // If API call fails, return mock data in the same format
      const wheel = getWheelById(id);
      if (!wheel) {
        throw new Error('Wheel not found');
      }
      return {
        data: wheel,
        status: 200,
        statusText: 'OK (Mock Data)',
        headers: {},
        config: {},
      };
    }
  },

  create: (data: any) => api.post('/wheels', data),
  update: (id: string, data: any) => api.put(`/wheels/${id}`, data),
  delete: (id: string) => api.delete(`/wheels/${id}`),
  getSpins: (id: string) => api.get(`/wheels/${id}/spins`),
  spin: (id: string, data: any) => api.post(`/wheels/${id}/spin`, data),

  // Shared wheel endpoints
  createWheelShare: (wheelId: string, shareSettings: any) =>
    api.post(`/wheels/${wheelId}/shares`, shareSettings),
  getWheelShares: (wheelId: string) => api.get(`/wheels/${wheelId}/shares`),
  getSharedWheel: (shareId: string) => api.get(`/shared/${shareId}`),
  updateWheelShare: (wheelId: string, shareId: string, settings: any) =>
    api.put(`/wheels/${wheelId}/shares/${shareId}`, settings),
  deleteWheelShare: (wheelId: string, shareId: string) =>
    api.delete(`/wheels/${wheelId}/shares/${shareId}`),
  spinSharedWheel: (shareId: string, data: any) => api.post(`/shared/${shareId}/spin`, data),
  getSharedWheelSpins: (shareId: string) => api.get(`/shared/${shareId}/spins`),
};

// Paydle API endpoints
export const paydlesApi = {
  // Create a new Paydle
  create: (data: any) => api.post('/paydles', data),

  // Get Paydle by share ID (for players)
  getByShareId: (shareId: string) => api.get(`/paydles/play/${shareId}`),

  // Get Paydle by access token (for creator)
  getByAccessToken: (accessToken: string) => api.get(`/paydles/manage/${accessToken}`),

  // Start a new attempt
  startAttempt: (paydleId: string, data: any) => api.post(`/paydles/${paydleId}/attempts`, data),

  // Submit a guess
  submitGuess: (paydleId: string, attemptId: string, guess: string) =>
    api.post(`/paydles/${paydleId}/attempts/${attemptId}/guess`, { guess }),

  // Get attempts for a Paydle (creator view)
  getAttempts: (paydleId: string, accessToken: string) =>
    api.get(`/paydles/${paydleId}/attempts`, { params: { accessToken } }),
};

export default api;
